import './ContactInformation.scss';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { FROM_CAMPAIGN_PAGE_QUERY_STRING } from '@components/CampaignRegisterComponent/RegisterEventForm/UserInfoForm/UpdatePhoneNumberText';
import LoadingMask from '@components/Share/LoadingMask';
import Message from '@components/Share/Message';
import DateOfBirth from '@components/SingleSignOn/DateOfBirth';
import { Yup } from '@components/SingleSignOn/RegisterLogin/Register/utils';
import { clearUpdateProfileSuccessStatus, updateUserProfile } from '@redux/actions/myAccount';
import { getUserProfile } from '@redux/actions/singleSignOn';
import SitecoreContextFactoryService from '@services/sitecoreContextFactoryService';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { HK_SITE_NAME, MY_SITE_NAME, SG_SITE_NAME, TW_SITE_NAME } from '@utils/constant';
import { useDidUpdateEffect } from '@utils/customsHook/useDidUpdateEffect';
import Global from '@utils/global';

import { getUserFullName } from '../../utils';
import { useCheckPOSAccount } from './hooks';
import ContactInformationModel from './models/ContactInformationModel';
import VerifyPhoneNumberUpdate from './VerifyPhoneNumberUpdate';

const ContactInfomation = ({ fields, isLoading, accountUser, message, isError, settingGlobal, isUpdateSuccess }) => {
  const dispatch = useDispatch();
  const [dataSources, setDataSources] = useState(null);

  const fieldPhone = useRef({
    code: '',
    value: ''
  });
  const [datePicker, setDatePicker] = useState(accountUser.DoB ? new Date(accountUser.DoB) : '');
  const [gender, setGender] = useState(accountUser.Gender);
  const messageObj = useSelector((state) => state.getMessageReducer.objMessages);
  const { search } = useLocation();
  const currentSiteName = SitecoreContextFactoryService.getValueContextItem('site')?.name;

  const checkPOSAccountService = useCheckPOSAccount();
  const [isCheckingPOSAccount, setIsCheckingPOSAccount] = useState(false);
  const [isPOSAccount, setIsPOSAccount] = useState(false);

  const isDisableDOBInput = useMemo(() => {
    const isDateValid = !isNaN(new Date(accountUser.DoB));
    const countryNotAllowEditDOD = [SG_SITE_NAME, MY_SITE_NAME, HK_SITE_NAME, TW_SITE_NAME];

    return (countryNotAllowEditDOD.includes(currentSiteName) && accountUser.DoB && isDateValid) || (isPOSAccount && !accountUser.DoB);
  }, [currentSiteName, accountUser, isPOSAccount]);

  const userFullName = useMemo(() => getUserFullName(accountUser, settingGlobal?.FullNameFormat), [settingGlobal, accountUser]);

  const formikValidationSchema = useMemo(() => {
    /** INFO: Helper function to create phone validation schema */
    const createPhoneValidation = (isRequired = false) => {
      const baseValidation = Yup.string().validatePhone({
        min: settingGlobal?.PhoneNumberMinimumMust || settingGlobal?.PhoneNumberMust,
        max: settingGlobal?.PhoneNumberMust,
        prefixCode: fieldPhone.current?.code || '',
        errorMsg: <Message messageCode='Msg_001.7' />
      });

      return isRequired ? baseValidation.required(<Message messageCode='Msg_020' />).trim() : baseValidation;
    };

    /** INFO: Create the schema object */
    const schemaFields = {
      firstName: Yup.string()
        .required(<Message messageCode='Msg_001.1' />)
        .matches(Global.nameValidateRegex(), messageObj?.['Msg_001.1'])
        .trim()
    };

    /** INFO: Add DOB validation conditionally */
    if (settingGlobal?.IsDoBRequired) {
      schemaFields.dob = !isDisableDOBInput
        ? Yup.string()
          .required(<Message messageCode='Msg_001.4' />)
          .trim()
        : Yup.string().trim();
    }

    /** INFO: Add gender validation conditionally */
    if (settingGlobal?.IsGenderRequired) {
      schemaFields.gender = Yup.string()
        .required(<Message messageCode='Msg_019' />)
        .trim();
    }

    /** INFO: Add phone validation based on complex conditions */
    if (settingGlobal?.PhoneNumberMust) {
      schemaFields.phone = createPhoneValidation(settingGlobal?.IsPhoneNumberRequired);
    } else if (settingGlobal?.IsPhoneNumberRequired) {
      schemaFields.phone = Yup.string()
        .required(<Message messageCode='Msg_020' />)
        .trim()
        .validatePhone({
          min: settingGlobal?.PhoneNumberMinimumMust || settingGlobal?.PhoneNumberMust,
          prefixCode: fieldPhone.current?.code || '',
          errorMsg: <Message messageCode='Msg_001.7' />
        });
    } else {
      schemaFields.phone = Yup.string().validatePhone({
        min: settingGlobal?.PhoneNumberMinimumMust || settingGlobal?.PhoneNumberMust,
        prefixCode: fieldPhone.current?.code || '',
        errorMsg: <Message messageCode='Msg_001.7' />
      });
    }

    return Yup.object(schemaFields);
  }, [
    settingGlobal?.IsDoBRequired,
    settingGlobal?.IsGenderRequired,
    settingGlobal?.PhoneNumberMust,
    settingGlobal?.IsPhoneNumberRequired,
    settingGlobal?.PhoneNumberMinimumMust,
    isDisableDOBInput,
    messageObj,
    fieldPhone.current?.code
  ]);

  useEffect(() => {
    /** INFO: re-fetch user profile when user is logged in and ensure their phone number is primary or secondary */
    if (accountUser) dispatch(getUserProfile({ isCache: false }));
  }, []);

  useEffect(() => {
    setDataSources(new ContactInformationModel().getData(fields || {}));
  }, []);

  useEffect(() => {
    const user = SitecoreContextFactoryService.getValueContextItem('user');
    if (settingGlobal && !user) {
      window.location.href = settingGlobal?.LoginLink;
    }
  }, [settingGlobal]);

  useEffect(() => {
    if (settingGlobal && settingGlobal?.PhoneNumberMust) {
      handleUpdateFieldPhoneValue();
    }
  }, [settingGlobal?.PhoneNumberMust]);

  useDidUpdateEffect(() => {
    if (isUpdateSuccess && search) {
      const fromPath = search?.replace(FROM_CAMPAIGN_PAGE_QUERY_STRING, '');

      window.location.href = fromPath;
    }
  }, [isUpdateSuccess, search]);

  useDidUpdateEffect(() => {
    if (isUpdateSuccess) {
      /** INFO: clear update profile success status after 500ms */
      const handleUpdateProfileStatus = () => dispatch(clearUpdateProfileSuccessStatus());

      setTimeout(handleUpdateProfileStatus, 500);

      /** INFO: update new updated phone value to fieldPhone */
      if (accountUser?.PhoneNumber !== `${fieldPhone.current.code}${fieldPhone.current.value}`) handleUpdateFieldPhoneValue();

      return () => clearTimeout(handleUpdateFieldPhoneValue);
    }
  }, [isUpdateSuccess, accountUser?.PhoneNumber, fieldPhone.current]);

  useDidUpdateEffect(() => {
    /** INFO: update new updated phone value to fieldPhone */
    handleUpdateFieldPhoneValue();
  }, [accountUser?.PhoneNumber]);

  useEffect(() => {
    // INFO: check account is POS or not
    (async () => {
      setIsCheckingPOSAccount(true);
      try {
        const checkPOSAccountRes = await checkPOSAccountService();
        if (checkPOSAccountRes) setIsPOSAccount(true);
      } catch (error) {
        setIsPOSAccount(false);
      } finally {
        setIsCheckingPOSAccount(false);
      }
    })();
  }, []);

  const handleSubmit = (values, formikHelper) => {
    const params = {
      EmailAddress: values.email,
      FirstName: values.firstName,
      PhoneNumber: `${fieldPhone.current.code}${values.phone}` || '',
      Gender: values.gender,
      DoB: values.dob,
      OTP: values.otp,
      ValidateOTP: !!values.otp
    };
    if (isDisableDOBInput) params.IsMyAccountPage = isDisableDOBInput;

    /** INFO: Add OTP validation before update profile if the phone number is changed */
    const savedProfilePhone = currentSiteName !== MY_SITE_NAME ? accountUser?.PhoneNumber : accountUser?.MYSitePhoneNumber;
    if (savedProfilePhone !== params.PhoneNumber && !values.otp) {
      return formikHelper.setFieldError('otp', <Message messageCode='Msg_038' />);
    }

    dispatch(updateUserProfile(params));
  };

  function handleUpdateFieldPhoneValue() {
    const phoneFormat = Global.splitPhoneNumberWithoutCode(settingGlobal.UseAreaCode, accountUser.PhoneNumber, settingGlobal.AreaCodes);

    fieldPhone.current.code = phoneFormat.code || settingGlobal?.AreaCodes?.[0]?.value || '';

    let phoneValue = phoneFormat.value || '';
    if (phoneValue.split('').filter((char) => char === '+').length) {
      phoneValue = phoneValue.replace('+', '');
    }

    fieldPhone.current.value = phoneValue;
  }

  /** @param {string} key - The key of the field to validate.
   * @param {any} values - The values of the form.
   * @returns {Promise<any>} - A promise that resolves to the validation result. */
  const validateField = async (key, values) => await formikValidationSchema.validateAt(key, values);

  return dataSources ? (
    <>
      <div className='contact-info'>
        <div className='container'>
          <div className='contact-info__wrap'>
            <Formik
              initialValues={{
                email: accountUser.Email,
                firstName: userFullName,
                phone: fieldPhone.current.value,
                phoneCode: fieldPhone.current.code,
                dob: datePicker,
                gender: accountUser.Gender,
                otp: '' // INFO: flag value to get inputted otp value
              }}
              enableReinitialize={true}
              validationSchema={formikValidationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, handleChange, values, setFieldValue, setErrors, setTouched }) => {
                return (
                  <Form className='contact-info__form'>
                    {message ? <span className={isError ? 'error-message text-center' : 'message-successful text-center'}>{message}</span> : ''}
                    <div className={Global.renderDynamicClass(errors.firstName && touched.firstName, 'form-group', 'input-error-validate')}>
                      <input
                        type='text'
                        onChange={handleChange}
                        value={values.firstName}
                        id='firstName'
                        className={Global.renderDynamicClass(values['firstName'], 'form-control form-control-lg', 'input-valid')}
                        name='firstName'
                        placeholder={dataSources['First Name Label'].value}
                      />
                      <label htmlFor='firstName' className='form-group__label'>
                        <Text field={dataSources['First Name Label']} />
                      </label>
                      {errors.firstName && touched.firstName && <span className='error-validate'>{errors.firstName}</span>}
                    </div>

                    <div className={Global.renderDynamicClass(errors.dob && touched.dob, 'form-group', 'input-error-validate')}>
                      <DateOfBirth
                        setFieldValue={setFieldValue}
                        dobLabel={dataSources['DoB Label']}
                        isRegister={false}
                        disabled={isDisableDOBInput}
                      />
                      {/* <Text field={dataSources['DoB Label']} tag='label' className='form-group__label' /> */}
                      {errors.dob && touched.dob && <span className='error-validate'>{errors.dob}</span>}
                    </div>

                    {settingGlobal?.GenderDatasource.length ? (
                      <div
                        className={Global.renderDynamicClass(
                          errors.gender && touched.gender,
                          'form-group form-group--gender',
                          'form-group--gender input-error-validate'
                        )}
                      >
                        <div>
                          {settingGlobal?.GenderDatasource.map((item) => {
                            return (
                              <div className='osim-radio' key={item.Key}>
                                <input
                                  type='radio'
                                  name='gender'
                                  value={item.Key}
                                  className='osim-radio-input'
                                  checked={gender === item.Key}
                                  readOnly
                                />
                                <label
                                  className='osim-radio-label'
                                  onClick={() => {
                                    setGender(item.Key);

                                    setFieldValue('gender', item.Key);
                                  }}
                                />
                                <label
                                  className='osim-radio-title'
                                  onClick={() => {
                                    setGender(item.Key);

                                    setFieldValue('gender', item.Key);
                                  }}
                                >
                                  {item.Value}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                        {errors.gender && touched.gender && <span className='error-validate'>{errors.gender}</span>}
                      </div>
                    ) : (
                      <></>
                    )}

                    <VerifyPhoneNumberUpdate
                      inputData={dataSources}
                      formikValues={values}
                      formikErrors={errors}
                      formikTouched={touched}
                      fieldPhoneCode={fieldPhone}
                      setFieldValue={setFieldValue}
                      setErrors={setErrors}
                      setTouched={setTouched}
                      formikValidateFieldValue={validateField}
                    />

                    <div className='form-group'>
                      <input
                        onChange={handleChange}
                        value={values.email}
                        type='email'
                        id='email'
                        className={Global.renderDynamicClass(values['phone'], 'form-control form-control-lg form-bg-gray', 'input-valid')}
                        name='email'
                        disabled
                      />
                    </div>

                    <div className='contact-info__btn text-center'>
                      <div className='contact-info__btn__item'>
                        <button type='submit' className='btn btn-outline-CTA1'>
                          <Text field={dataSources['Save Change Text']} />
                        </button>
                      </div>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
        {isLoading && <LoadingMask />}
      </div>
      {isCheckingPOSAccount ? <LoadingMask /> : <></>}
    </>
  ) : (
    <></>
  );
};

ContactInfomation.propTypes = {
  accountUser: PropTypes.object,
  fields: PropTypes.object,
  isLoading: PropTypes.bool,
  message: PropTypes.string,
  isError: PropTypes.bool,
  settingGlobal: PropTypes.any,
  isUpdateSuccess: PropTypes.bool
};

const mapStateToProps = (state) => ({
  accountUser: state.singleSignOnReducer.userInfoReducer.accountUser,
  isLoading: state.singleSignOnReducer.updateAccountReducer.isLoading,
  message: state.singleSignOnReducer.updateAccountReducer.message,
  isError: state.singleSignOnReducer.updateAccountReducer.isError,
  settingGlobal: state.settingGlobalReducer.settingGlobal,
  isUpdateSuccess: state.singleSignOnReducer.updateAccountReducer.isUpdateSuccess
});

export default connect(mapStateToProps)(ContactInfomation);
